import BaseFile from '@/modules/common/models/file/base-file.ts';
import UploadFile from '@/modules/common/models/file/upload-file.ts';
import { uniqueId } from 'lodash-es';
import { MAX_FILE_SIZE, MAX_FILE_SIZE_MB } from '@/modules/common/consts/file.ts';
import { IdPrefix } from '@/modules/common/enums/id-prefix.ts';
import { notification } from '@/modules/common/composables/use-notification.ts';
import { getFileSize } from '@/modules/common/utils/file-size.ts';
import { getSystemPrefix } from '@/modules/common/utils/system-prefix.ts';
import i18n from '@/i18n.ts';

const PRECISION = 2;

export default class ExternalFile extends UploadFile {
  base64: string;

  constructor(file: File, base64: string) {
    const prefix = getSystemPrefix(file.name);

    super(uniqueId(IdPrefix.ExternalFile), file.name.slice(prefix.length), file.size);
    this.markPrefixStripped(prefix);
    this.base64 = base64;
  }

  static async createList(files: File[]) {
    const valid = ExternalFile.getValid(files);
    const results = await Promise.allSettled(valid.map((file) => ExternalFile.create(file)));

    return results.filter((result) => result.status === 'fulfilled').map((result) => result.value);
  }

  static async create(file: File) {
    try {
      const base64 = await BaseFile.toBase64(file);
      return new ExternalFile(file, base64);
    } catch (error) {
      notification.warning({ key: 'common.notification.warning.file.unreadable', params: { name: file.name } });

      throw error;
    }
  }

  static getValid(files: File[]) {
    return files.filter((file) => {
      if (file.size <= 0) {
        notification.warning({ key: 'common.notification.warning.file.empty', params: { name: file.name } });

        return false;
      }

      if (file.size > MAX_FILE_SIZE) {
        const { value, unit } = getFileSize(file.size);

        const size = `${i18n.global.n(value, { minimumFractionDigits: 0, maximumFractionDigits: PRECISION })} ${i18n.global.t(unit)}`;

        notification.warning({
          key: 'common.notification.warning.file.tooLarge',
          params: {
            name: file.name,
            size,
            max: MAX_FILE_SIZE_MB,
          },
        });

        return false;
      }

      return true;
    });
  }
}
